/**
 * Loudness measurement for one-shots.
 *
 * Programme loudness (BS.1770 integrated) is built for minutes of music and reads a 60 ms
 * hi-hat as near-silence, so it cannot balance a kit. What is measured instead is the
 * K-weighted level of the *loudest quarter second* of the sound — or of the whole sound, when
 * it is shorter than that. The weighting filters are the standard ones; only the integration
 * changes.
 *
 * Two things fall out of that choice, and both were measured rather than assumed. A long
 * reverb tail no longer drags the average down, so a clap in a big room is levelled by its
 * clap and not by its room. And a short sound is still averaged over its own length rather
 * than over a fixed window, so a 40 ms hat is not penalised 8 dB for being a hat.
 */

import { Biquad, frameCount } from '../offline-render';

/**
 * Longest window the measurement integrates over. A quarter second is about where the ear
 * stops summing energy and starts hearing two events, and it is short enough that the decay
 * of a two-second 808 cannot dilute its attack.
 */
const MAXIMUM_WINDOW = 0.25;

/**
 * Floor on the window. Without it a 3 ms click divides its energy by 3 ms and reads louder
 * than everything else in the kit.
 */
const MINIMUM_WINDOW = 0.02;

/** Share of the total energy that defines the sound's active length. */
const ENERGY_SHARE = 0.99;

/**
 * BS.1770-4 pre-filter: a +4 dB high shelf standing in for the head, then a high-pass that
 * discounts the sub-bass the ear is least sensitive to. Built from the cookbook coefficients
 * rather than the tabulated 48 kHz ones, so the measurement is the same at any render rate.
 */
export function applyKWeighting(samples: Float32Array, sampleRate: number): void {
  const shelf = new Biquad().set('highShelf', 1681.974450955533, 0.7071752369554196, sampleRate, 3.999843853973347);
  const highPass = new Biquad().set('highPass', 38.13547087602444, 0.5003270373238773, sampleRate);
  for (let index = 0; index < samples.length; index++) samples[index] = shelf.process(samples[index]!);
  for (let index = 0; index < samples.length; index++) samples[index] = highPass.process(samples[index]!);
}

/** K-weighted level of the sound's loudest window, in dB on the LUFS scale. */
export function activeLevelDB(channels: readonly Float32Array[], sampleRate: number): number {
  const frames = frameCount(channels);
  if (frames === 0) return Number.NEGATIVE_INFINITY;

  const energy = new Float64Array(frames);
  for (const channel of channels) {
    const weighted = channel.slice(0, frames);
    applyKWeighting(weighted, sampleRate);
    // Channel gains in BS.1770 are 1.0 for left and right, so the channels simply sum.
    // Everything in this library is stereo, so the +3 dB that gives a dual-mono file is a
    // constant and cancels out of every comparison.
    for (let index = 0; index < frames; index++) energy[index]! += weighted[index]! * weighted[index]!;
  }

  // Running integral, so the sliding window costs one subtraction per frame instead of one
  // sum per position.
  const integral = new Float64Array(frames + 1);
  for (let index = 0; index < frames; index++) integral[index + 1] = integral[index]! + energy[index]!;
  const total = integral[frames]!;
  if (total <= 0) return Number.NEGATIVE_INFINITY;

  let activeFrames = frames;
  const limit = total * ENERGY_SHARE;
  for (let index = 0; index < frames; index++) {
    if (integral[index + 1]! >= limit) {
      activeFrames = index + 1;
      break;
    }
  }

  const window = Math.min(
    Math.max(Math.min(activeFrames, Math.trunc(MAXIMUM_WINDOW * sampleRate)), Math.trunc(MINIMUM_WINDOW * sampleRate)),
    frames,
  );
  if (window <= 0) return Number.NEGATIVE_INFINITY;

  let loudest = 0;
  for (let start = 0; start + window <= frames; start++) {
    const value = integral[start + window]! - integral[start]!;
    if (value > loudest) loudest = value;
  }
  const meanSquare = loudest / window;
  if (meanSquare <= 0) return Number.NEGATIVE_INFINITY;
  return -0.691 + 10 * Math.log10(meanSquare);
}
